<template>
  <div>
    <b-loading is-full-page v-model="isLoading"></b-loading>
    <b-notification
        v-model="isError"
        type="is-danger"
        aria-close-label="Close notification"
        :closable="false">
        <b>Error fetching user!</b><br />Try refreshing the page. If the error persists check your network connection or let BM know.
    </b-notification>

    <!-- Sticky Top Bar with Breadcrumb -->
    <TopBar
      v-if="!isLoading && !isError"
      parentTitle="Users"
      :currentTitle="name || email || id"
      :backRoute="{ name: 'UserListView' }"
    >
      <span v-if="isAnonymous" class="membership-tag is-anon">Anonymous</span>
      <span class="membership-tag" :class="membership.tone">{{ membership.label }}</span>
      <span v-if="membership.detail" class="membership-detail" :class="membership.detailTone">
        {{ membership.detail }} {{ formatDate(membership.expiryDate) }}
      </span>
      <b-button
        tag="a"
        :href="mixpanelUrl"
        target="_blank"
        rel="noopener"
        outlined
        type="is-primary"
        size="is-small"
        icon-left="chart-line">Mixpanel Profile</b-button>
    </TopBar>

    <div v-if="!isLoading && !isError" class="page-content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="page-title-group">
          <h1 class="page-title">{{ name || 'Unnamed user' }}</h1>
          <p class="page-subtitle">
            {{ email || (isAnonymous ? 'Anonymous account — no email yet' : 'No email on record') }}
          </p>
        </div>
      </div>

      <div class="user-grid">
        <FeedsCard title="Account">
          <div class="detail-row">
            <span class="detail-label">Name</span>
            <span class="detail-value">{{ name || '—' }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Email</span>
            <span class="detail-value">
              <CopyText :value="email" label="Email" />
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">User ID</span>
            <span class="detail-value">
              <CopyText :value="uid" label="User ID" mono />
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Account type</span>
            <span class="detail-value">
              <span class="membership-tag" :class="isAnonymous ? 'is-anon' : 'is-member'">
                {{ isAnonymous ? 'Anonymous' : 'Registered' }}
              </span>
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">First seen</span>
            <span class="detail-value">{{ formatDateTime(createdAt) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Last seen</span>
            <span class="detail-value">{{ formatDateTime(lastSeen) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Shopping list ID</span>
            <span class="detail-value">
              <CopyText :value="shoppingListId" label="Shopping list ID" mono />
            </span>
          </div>
          <div v-if="aliases.length" class="detail-row">
            <span class="detail-label">Aliases</span>
            <span class="detail-value alias-list">
              <CopyText v-for="alias in aliases" :key="alias" :value="alias" label="Alias" mono />
            </span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Main goal</span>
            <span class="detail-value">{{ formatValue(mainGoal) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Mixpanel</span>
            <span class="detail-value">
              <a :href="mixpanelUrl" target="_blank" rel="noopener">View profile</a>
            </span>
          </div>
        </FeedsCard>

        <FeedsCard title="Subscription">
          <template v-if="subscriptions.length">
            <div v-for="sub in subscriptions" :key="sub.productId" class="sub-block">
              <div class="sub-heading">
                <code class="id-code">{{ sub.productId }}</code>
                <span class="bool-tag" :class="sub.isActive ? 'is-yes' : 'is-no'">
                  {{ sub.isActive ? 'Active' : 'Expired' }}
                </span>
                <span v-if="sub.isTrial" class="bool-tag is-trial">Trial</span>
                <span v-if="sub.isSandbox" class="bool-tag is-no">Sandbox</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Period type</span>
                <span class="detail-value">{{ formatValue(sub.periodType) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Set to renew</span>
                <span class="detail-value">
                  <span v-if="isBoolean(sub.willRenew)" class="bool-tag" :class="sub.willRenew ? 'is-yes' : 'is-no'">
                    {{ sub.willRenew ? 'Yes' : 'No' }}
                  </span>
                  <template v-else>—</template>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">{{ sub.willRenew ? 'Renews' : 'Expires' }}</span>
                <span class="detail-value">{{ formatDateTime(sub.expiresDate) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Purchased</span>
                <span class="detail-value">{{ formatDateTime(sub.purchaseDate) }}</span>
              </div>
              <div v-if="sub.originalPurchaseDate" class="detail-row">
                <span class="detail-label">First purchased</span>
                <span class="detail-value">{{ formatDateTime(sub.originalPurchaseDate) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Store</span>
                <span class="detail-value">{{ formatValue(sub.store) }}</span>
              </div>
              <div v-if="sub.unsubscribeDetectedAt" class="detail-row">
                <span class="detail-label">Cancelled</span>
                <span class="detail-value">{{ formatDateTime(sub.unsubscribeDetectedAt) }}</span>
              </div>
              <div v-if="sub.gracePeriodExpiresAt" class="detail-row">
                <span class="detail-label">Grace period ends</span>
                <span class="detail-value">{{ formatDateTime(sub.gracePeriodExpiresAt) }}</span>
              </div>
              <div v-if="sub.billingIssuesDetectedAt" class="detail-row">
                <span class="detail-label">Billing issue</span>
                <span class="detail-value has-text-danger">{{ formatDateTime(sub.billingIssuesDetectedAt) }}</span>
              </div>
            </div>
          </template>
          <p v-else class="empty-note">No subscriptions on this account.</p>
        </FeedsCard>

        <FeedsCard title="Marketing Consent">
          <template v-if="consentRows.length">
            <div v-for="row in consentRows" :key="row.label" class="detail-row">
              <span class="detail-label">{{ row.label }}</span>
              <span class="detail-value">
                <span v-if="isBoolean(row.value)" class="bool-tag" :class="row.value ? 'is-yes' : 'is-no'">
                  {{ row.value ? 'Yes' : 'No' }}
                </span>
                <template v-else>{{ formatValue(row.value) }}</template>
              </span>
            </div>
          </template>
          <p v-else class="empty-note">No marketing consent settings on this account.</p>
        </FeedsCard>
      </div>

      <!-- Escape hatch while the users schema is still being confirmed -->
      <FeedsCard title="Raw Document">
        <template #header-actions>
          <b-button size="is-small" type="is-text" @click="showRaw = !showRaw">
            {{ showRaw ? 'Hide' : 'Show' }}
          </b-button>
        </template>
        <pre v-if="showRaw" class="raw-json">{{ rawJson }}</pre>
      </FeedsCard>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import CopyText from "@/components/common/CopyText"
import FeedsCard from "@/components/common/FeedsCard"
import TopBar from "@/components/layout/TopBar"
import {
  formatDate, formatDateTime, isAnonymousUser, mainGoal, marketingConsentRows,
  membershipStatus, mixpanelProfileUrl, shoppingListId, subscriptionList, userAliases,
  userCreatedAt, userEmail, userLastSeen, userName, userUid
} from '@/utils/userFields'

export default {
  name: 'UserView',
  components: {
    CopyText,
    FeedsCard,
    TopBar
  },
  props: ['id'],
  data() {
    return {
      user: {},
      isLoading: true,
      isError: false,
      showRaw: false
    }
  },
  computed: {
    name() { return userName(this.user) },
    email() { return userEmail(this.user) },
    createdAt() { return userCreatedAt(this.user) },
    lastSeen() { return userLastSeen(this.user) },
    uid() { return userUid(this.user, this.id) },
    aliases() { return userAliases(this.user) },
    shoppingListId() { return shoppingListId(this.user) },
    mainGoal() { return mainGoal(this.user) },
    isAnonymous() { return isAnonymousUser(this.user, this.id) },
    membership() { return membershipStatus(this.user) },
    subscriptions() { return subscriptionList(this.user) },
    consentRows() { return marketingConsentRows(this.user) },
    mixpanelUrl() { return mixpanelProfileUrl(this.uid) },
    rawJson() {
      return JSON.stringify(this.user, this.replacer, 2)
    }
  },
  created() {
    db.collection('users')
      .doc(this.id)
      .get()
      .then(snapshot => {
        if (snapshot.exists) {
          this.user = snapshot.data()
        } else {
          this.isError = true
        }
        this.isLoading = false
      })
      .catch((error) => {
        this.isLoading = false
        this.isError = true
        this.$buefy.notification.open({ type: 'is-danger', message: `Error fetching user: ${error}` })
      })
  },
  methods: {
    formatDate,
    formatDateTime,
    isBoolean(value) {
      return typeof value === 'boolean'
    },
    formatValue(value) {
      if (value === undefined || value === null || value === '') return '—'
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (typeof value === 'object') {
        if (value.toDate) return formatDateTime(value)
        return JSON.stringify(value)
      }
      return String(value)
    },
    // Timestamps and DocumentReferences do not survive JSON.stringify usefully
    replacer(key, value) {
      if (value && typeof value === 'object') {
        if (typeof value.seconds === 'number' && typeof value.nanoseconds === 'number') {
          return new Date(value.seconds * 1000).toISOString()
        }
        if (value.firestore && value.path) return `→ ${value.path}`
      }
      return value
    }
  }
}
</script>

<style scoped>
.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-light);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  font-size: 13px;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.detail-value {
  font-size: 14px;
  color: var(--text-primary);
  text-align: right;
  word-break: break-word;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.empty-note {
  font-size: 14px;
  color: var(--text-muted);
  padding: 8px 0;
}

.id-code {
  background-color: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  color: var(--text-secondary);
}

.bool-tag,
.membership-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.bool-tag.is-yes,
.membership-tag.is-member,
.membership-tag.is-lifetime {
  background: rgba(0, 133, 122, 0.12);
  color: var(--feeds-green, #00857a);
}

.bool-tag.is-no,
.membership-tag.is-free,
.membership-tag.is-expired {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
}

.membership-tag.is-trial {
  background: rgba(255, 120, 73, 0.14);
  color: var(--feeds-orange, #ff7849);
}

/* Anonymous reads as a state, not a warning — cool tone, away from the
   green/orange the membership pills use */
.membership-tag.is-anon {
  background: rgba(96, 165, 250, 0.16);
  color: #3574d4;
}

.membership-detail {
  font-size: 12px;
  color: var(--text-secondary);
}

.membership-detail.is-cancelling {
  color: var(--feeds-orange, #ff7849);
  font-weight: 600;
}

.sub-block + .sub-block {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-light);
}

.sub-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.alias-list {
  flex-wrap: wrap;
  gap: 4px;
}

.bool-tag.is-trial {
  background: rgba(255, 149, 0, 0.14);
  color: var(--feeds-orange, #f5871f);
}

.raw-json {
  background: var(--bg-tertiary);
  border-radius: var(--radius-sm, 6px);
  padding: 16px;
  font-size: 12px;
  line-height: 1.5;
  max-height: 480px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
